import { format } from 'date-fns';

import { IFeatureWalmartUiQuery } from 'generated/sanity-graphql';

import { t } from '../../../intl/translations';
import { LoyaltyUser } from '../types';

import { getNextQuarterDate, getTomorrowDate } from './helpers';
import { isWalmartQuarterlyOfferGroup } from './walmart-offer-group';

export { isWalmartQuarterlyOfferGroup, WALMART_QUARTERLY_OFFER_TYPE } from './walmart-offer-group';

enum WalmartEngineOfferIds {
  QUARTERLY_FREE_WHOPPER = '4e789a40-3de8-4cc5-b06e-8b0bc77e9579',
  DAILY_TWENTY_FIVE_PERCENT_OFF = '1659a35d-5933-410c-9718-736c3b78c122',
  WALMART_NEW_HALLOWEEN25_OFFER = '9bde2b00-e353-45bc-932e-2a892c60bb4d',
}

enum WalmartSanityOfferIds {
  QUARTERLY_FREE_WHOPPER = '8f059309-e9f2-4c3f-92a6-9dea0a5fce06',
  DAILY_TWENTY_FIVE_PERCENT_OFF = 'a48ac94b-298b-49ae-a1fe-ef576b644712',
  WALMART_NEW_HALLOWEEN25_OFFER = '243687c7-6331-465c-82a6-07ef5b9e0be5',
}

const walmartOfferEngineIds: string[] = [
  WalmartEngineOfferIds.WALMART_NEW_HALLOWEEN25_OFFER,
  WalmartEngineOfferIds.QUARTERLY_FREE_WHOPPER,
  WalmartEngineOfferIds.DAILY_TWENTY_FIVE_PERCENT_OFF,
];

const walmartOfferSanityIds: string[] = [
  WalmartSanityOfferIds.QUARTERLY_FREE_WHOPPER,
  WalmartSanityOfferIds.DAILY_TWENTY_FIVE_PERCENT_OFF,
  WalmartSanityOfferIds.WALMART_NEW_HALLOWEEN25_OFFER,
];

export const isWalmartOffer = (offer: Record<string, any>): boolean => {
  if (isWalmartQuarterlyOfferGroup(offer)) {
    return true;
  }
  return [...walmartOfferEngineIds, ...walmartOfferSanityIds].includes(
    offer.id || offer._id || offer.loyaltyEngineId
  );
};

export const isAnyWalmartOffer = (offer: Record<string, any>): boolean => {
  return isWalmartOffer(offer);
};

export const isActiveWalmartUser = (loyaltyUser: LoyaltyUser): boolean => {
  return (
    loyaltyUser?.partnerUserData?.some(
      data => data.partner === 'WALMART' && data.status === 'ACTIVE'
    ) || false
  );
};

export const isWalmartPaidMember = (loyaltyUser: LoyaltyUser): boolean => {
  return !!loyaltyUser?.partnerUserData?.find(
    data => data?.partnerData?.userState?.toLowerCase() === 'paid'
  );
};

/**
 * Push Walmart Offers to the top of the list
 */
export const pushWalmartOffersToTop = (walmartSeasonalOffersIds: string[]) => (
  a: Record<string, any>,
  b: Record<string, any>
) => {
  const isWalmartSeasonalOffer = (item: Record<string, any>) =>
    walmartSeasonalOffersIds.includes(item._id);

  if (isWalmartSeasonalOffer(a) && !isWalmartSeasonalOffer(b)) {
    return -1;
  } else if (!isWalmartSeasonalOffer(a) && isWalmartSeasonalOffer(b)) {
    return 1;
  }

  if (isWalmartOffer(a) && !isWalmartOffer(b)) {
    return -1;
  } else if (!isWalmartOffer(a) && isWalmartOffer(b)) {
    return 1;
  }
  return 0;
};

// TODO: Remove pushWalmartNewHalloween25OfferToTop after the Halloween promo ends
export const pushWalmartNewHalloween25OfferToTop = (
  a: Record<string, any>,
  b: Record<string, any>
) => {
  const halloween25OfferIds = [
    WalmartEngineOfferIds.WALMART_NEW_HALLOWEEN25_OFFER,
    WalmartSanityOfferIds.WALMART_NEW_HALLOWEEN25_OFFER,
  ];
  if (halloween25OfferIds.includes(a._id)) {
    return -1;
  } else if (halloween25OfferIds.includes(b._id)) {
    return 1;
  }
  return 0;
};

export const pushWalmartQuarterlyWhooperToBottom = (
  a: Record<string, any>,
  b: Record<string, any>
) => {
  const quarterlyWhooperIds = [
    WalmartEngineOfferIds.QUARTERLY_FREE_WHOPPER,
    WalmartSanityOfferIds.QUARTERLY_FREE_WHOPPER,
  ];

  if (
    quarterlyWhooperIds.includes(a.id || a._id || a.loyaltyEngineId) ||
    isWalmartQuarterlyOfferGroup(a)
  ) {
    return 1;
  } else if (
    quarterlyWhooperIds.includes(b.id || b._id || b.loyaltyEngineId) ||
    isWalmartQuarterlyOfferGroup(b)
  ) {
    return -1;
  }
  return 0;
};

export const getWalmartOfferUnavailableMessage = (sanityOfferId: string): string => {
  const date =
    sanityOfferId === WalmartSanityOfferIds.QUARTERLY_FREE_WHOPPER
      ? getNextQuarterDate()
      : getTomorrowDate();
  const message = t('availableAgainOn', {
    date: format(date, 'P'),
  });
  return message;
};

export const getWalmartSeasonalConfigOfferIds = (
  loyaltyWalmart: IFeatureWalmartUiQuery['Walmart']
) => {
  return (
    loyaltyWalmart?.seasonalPromoConfig?.configOffers
      ?.map(offer => offer?._id)
      .filter((id): id is string => !!id) ?? []
  );
};

export const PARTNER_USER_ALREADY_LINKED = 'PARTNER_USER_ALREADY_LINKED';
export const LOYALTY_ID_ALREADY_LINKED = 'LOYALTY_ID_ALREADY_LINKED';

export const WALMART_PLUS_URL =
  'https://www.walmart.com/plus/program?benefitName=dining-benefit-burgerking&programId=burgerking';
